import { Injectable } from '@angular/core';
import * as LDClient from 'launchdarkly-js-client-sdk';
import { Observable, ReplaySubject } from 'rxjs';

import { AppExtensionService } from '@adw/projects/aca-shared/src/public-api';
import { WkAppConfigService } from './wk-app-config.service';

import { CustomExtensionService } from '../model/extension.model';

@Injectable({
  providedIn: 'root',
})
export class LDClientService {
  private ldcInitialized = false;
  private ldClient: LDClient.LDClient;
  private readonly _ldClientInitializing = new ReplaySubject<boolean>(1);

  constructor(
    private readonly appConfigService: WkAppConfigService,
    extension: AppExtensionService
  ) {
    (extension as CustomExtensionService).ldClient = this;
  }

  private async initializeLDClient(context: LDClient.LDContext = null) {
    const key = this.appConfigService.get<string>('ldcKey').replace(/-/g, '');
    if (!key) {
      this._ldClientInitializing.next(false);
      return;
    }
    if (!context) {
      context = {
        kind: 'user',
        anonymous: true,
      };
    }
    this.ldClient = LDClient.initialize(key, context);
    try {
      const initPromise = this.ldClient.waitForInitialization();
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('LD timeout')), 5000)
      );
      await Promise.race([initPromise, timeoutPromise]);
      this.ldcInitialized = true;
    } catch {
      this.ldcInitialized = false;
    } finally {
      this._ldClientInitializing.next(false);
    }
  }

  public ldcInitializing(initializing: boolean): void {
    this._ldClientInitializing.next(initializing);
  }

  public isInitializing(): Observable<boolean> {
    return this._ldClientInitializing.asObservable();
  }

  public variation<T>(flagKey: string, defaultValue: T = null): T {
    if (!this.ldcInitialized) {
      return null;
    }
    return this.ldClient.variation(flagKey, defaultValue);
  }

  public async identify(context: LDClient.LDContext): Promise<void> {
    try {
      if (!this.ldcInitialized) {
        await this.initializeLDClient(context);
      } else {
        await this.ldClient.identify(context);
      }
    } catch {
      this.ldcInitialized = false;
    }
  }

  public async close(): Promise<void> {
    if (!this.ldcInitialized) {
      return;
    }
    this.ldcInitialized = false;
    await this.ldClient.close();
    this.ldClient = null;
  }
}
